import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { MarketingShell, M } from "@/components/MarketingShell";
import { getStudentDirectory } from "@/lib/directory.functions";

const dirQuery = queryOptions({ queryKey: ["directory", "public"], queryFn: () => getStudentDirectory() });

const DESC =
  "Real students using Tiger's Eye — by university and course. Every listing is opt-in and shows first names only.";

export const Route = createFileRoute("/students")({
  head: () => ({
    meta: [
      { title: "Student directory — Tiger's Eye" },
      { name: "description", content: DESC },
      { property: "og:title", content: "Student directory — Tiger's Eye" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tigerseye.app/students" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/students" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(dirQuery),
  errorComponent: () => (
    <MarketingShell><p>Couldn't load the directory. Please try again shortly.</p></MarketingShell>
  ),
  notFoundComponent: () => <MarketingShell><p>Not found.</p></MarketingShell>,
  component: StudentsPage,
});

function StudentsPage() {
  const { data } = useSuspenseQuery(dirQuery);
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = data.filter((s) =>
      !term || [s.institution, s.course].some((v) => v?.toLowerCase().includes(term)),
    );
    const map = new Map<string, typeof list>();
    for (const s of list) {
      const k = s.institution || "University not given";
      map.set(k, [...(map.get(k) ?? []), s]);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [data, q]);

  return (
    <MarketingShell>
      <p className="text-xs uppercase tracking-[0.2em]" style={{ color: M.BRASS }}>Student directory</p>
      <h1 className="mt-3 text-4xl" style={{ fontFamily: M.SERIF }}>Real students, real courses</h1>
      <p className="mt-4" style={{ color: M.INK_SOFT }}>
        {data.length} student{data.length === 1 ? "" : "s"} have chosen to be listed. Listings are
        opt-in and show first names only — no contact details.
      </p>
      <p className="mt-2 text-sm" style={{ color: M.INK_FAINT }}>
        Creator or journalist? Get in touch through the{" "}
        <Link to="/faq" className="underline">FAQ</Link> and we'll pass your message on to
        students who want to take part.
      </p>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by university or course"
        aria-label="Search by university or course"
        className="mt-8 w-full rounded-md border px-3 py-2 text-sm"
        style={{ borderColor: M.RULE, background: M.SURFACE, color: M.INK }}
      />

      {groups.length === 0 ? (
        <p className="mt-10 text-sm" style={{ color: M.INK_SOFT }}>
          No students listed yet. Signed up?{" "}
          <Link to="/profile" className="underline">Add yourself from your account page</Link>.
        </p>
      ) : (
        <div className="mt-10 space-y-10">
          {groups.map(([inst, people]) => (
            <section key={inst}>
              <h2 className="text-xl" style={{ fontFamily: M.SERIF }}>
                {inst} <span className="text-sm" style={{ color: M.INK_FAINT }}>· {people.length}</span>
              </h2>
              <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                {people.map((s, i) => (
                  <li key={i} className="rounded-md border p-4" style={{ borderColor: M.RULE, background: M.SURFACE }}>
                    <p className="font-semibold" style={{ color: M.INK }}>{s.firstName}</p>
                    <p className="text-sm" style={{ color: M.INK_SOFT }}>
                      {[s.course, s.year].filter(Boolean).join(" · ") || "Course not given"}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <p className="mt-12 text-sm" style={{ color: M.INK_SOFT }}>
        Using Tiger's Eye? <Link to="/profile" className="underline">List yourself</Link> ·
        Not yet? <Link to="/free-review" className="underline">Claim a free review</Link>
      </p>
    </MarketingShell>
  );
}
